import { TextField, type TextFieldProps } from '@mui/material';
import { useId } from 'react';

import {
  FormField,
  HelpTooltip,
  type HelpTooltipProps,
  type RequireLabelWithOptionalHelpTooltipOrAria,
  getLabelOrAriaProps,
  isDefined,
  isTruthy,
  useFormFieldState,
} from 'common';

type TextFieldControlProps = Omit<Partial<TextFieldProps<'outlined'>>, 'label' | 'aria-label'> & {
  name: string;
  helperText?: string;
  disabled?: boolean;
  helpTooltipProps?: HelpTooltipProps;
  readOnly?: boolean;
  required?: boolean;
} & RequireLabelWithOptionalHelpTooltipOrAria;

export function TextFieldControl(props: TextFieldControlProps) {
  const {
    name,
    label,
    helperText,
    'aria-label': ariaLabel,
    helpTooltipProps,
    readOnly,
    required,
    slotProps,
    ...textFieldProps
  } = props;
  const fieldId = useId();

  const { fieldProps, isErrorVisible, errorMessage } = useFormFieldState<string | number>({ name });

  return (
    <FormField
      id={fieldId}
      {...getLabelOrAriaProps({ label, 'aria-label': ariaLabel })}
      helperText={helperText}
      required={required}
      error={isErrorVisible ? errorMessage : undefined}
      labelAdornment={
        isDefined(helpTooltipProps) ? (
          <HelpTooltip {...helpTooltipProps} tooltipProps={{ placement: 'top' }} />
        ) : undefined
      }
    >
      <TextField
        id={fieldId}
        {...textFieldProps}
        slotProps={{
          ...slotProps,
          htmlInput: {
            ...(typeof slotProps?.htmlInput !== 'function' ? slotProps?.htmlInput : undefined),
            'aria-label': ariaLabel,
            'aria-required': required,
            readOnly: isTruthy(readOnly),
          },
        }}
        {...fieldProps}
        value={fieldProps.value ?? ''}
        onChange={(event) => {
          fieldProps.onChange?.(event);
          textFieldProps.onChange?.(event);
        }}
        onBlur={(event) => {
          fieldProps.onBlur?.(event);
          textFieldProps.onBlur?.(event);
        }}
        disabled={props.disabled}
        error={isErrorVisible}
      />
    </FormField>
  );
}
